import { TFunction } from 'react-i18next';
import { differenceInCalendarDays, isDate, parseISO } from 'date-fns';
import Instant from 'ts-time/Instant';
import { ZoneId } from 'ts-time/Zone';
import {
  CalendarLead,
  StackedCalendarMode,
} from 'pages/stackedCalendar/Calendar.types';
import { Property } from 'models/Properties';
import { LeadsSearchResponseItem } from 'components/forms/domain/lead/LeadSearchField.types';
import { HasCleaningFeeItem } from 'models/hasOrderItems/HasOrderItems';
import { getTimezoneId } from 'utils/timezones';
import { Channel, ChannelSettings } from '../../models/Channels';
import { IntegrationEnum } from '../../models/InternalIntegrationSetting';
import {
  AffiliateToColorMap,
  ChannelToSourceMap,
  Lead,
  LeadChannel,
  LeadSavingResult,
  LeadSource,
  LeadStatus,
  LeadType,
  LeadColor,
} from '../../models/Leads';
import { ThreadRelatedLead } from '../../models/inbox/Threads';
import { normalizeKey } from '../localeUtils';
import { TemplateName } from '../../components/domain/template/modal/TemplateModal.types';
import {
  IMPORT_SOURCES,
  SOURCES_ORDER_NOT_EDITABLE,
  TRANSPARENT_STATUSES,
} from './leadUtils.constant';

function parseUtcDateTimeInLeadTimezone(
  lead: Lead,
  utcDateTimeString: string,
): Date {
  const timezoneId = getTimezoneId({ dateString: lead.checkInDateTimeString });

  if (!timezoneId) {
    return parseISO(utcDateTimeString);
  }

  try {
    return Instant.parse(utcDateTimeString).atZone(ZoneId.of(timezoneId))
      .dateTime.nativeLocal;
  } catch {
    return parseISO(utcDateTimeString);
  }
}

export function getLeadCheckInDate(
  lead: Lead | CalendarLead | LeadSavingResult | LeadsSearchResponseItem,
) {
  if (!lead.checkInDate || !isDate(lead.checkInDate)) {
    lead.checkInDate = parseISO(lead.checkInLocalDateTime);
  }

  return lead.checkInDate;
}

export function getLeadCheckOutDate(
  lead: Lead | CalendarLead | LeadSavingResult | LeadsSearchResponseItem,
) {
  if (!lead.checkOutDate || !isDate(lead.checkOutDate)) {
    lead.checkOutDate = parseISO(lead.checkOutLocalDateTime);
  }

  return lead.checkOutDate;
}

export function getLeadCheckInUtcDate(
  lead: Lead | CalendarLead | LeadSavingResult,
) {
  if (!lead.checkInUtcDate || !isDate(lead.checkInUtcDate)) {
    // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    lead.checkInUtcDate = parseISO(lead.checkInUtcDateTime);
  }

  return lead.checkInUtcDate;
}

export function getLeadCheckOutUtcDate(
  lead: Lead | CalendarLead | LeadSavingResult,
) {
  if (!lead.checkOutUtcDate || !isDate(lead.checkOutUtcDate)) {
    // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    lead.checkOutUtcDate = parseISO(lead.checkOutUtcDateTime);
  }

  return lead.checkOutUtcDate;
}

export function getLeadArrivalDate(lead: Lead) {
  if (!lead.arrivalDate || !isDate(lead.arrivalDate)) {
    // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    lead.arrivalDate = parseISO(lead.arrivalLocalDateTime);
  }

  return lead.arrivalDate;
}

export function getLeadDepartureDate(lead: Lead) {
  if (!lead.departureDate || !isDate(lead.departureDate)) {
    // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    lead.departureDate = parseISO(lead.departureLocalDateTime);
  }

  return lead.departureDate;
}

export function getLeadCreatedDate(lead: Lead) {
  if (
    lead.createdDateTimeString &&
    (!lead.createdDate || !isDate(lead.createdDate))
  ) {
    lead.createdDate = parseUtcDateTimeInLeadTimezone(
      lead,
      lead.createdDateTimeString,
    );
  }

  return lead.createdDate;
}

export function getLeadBookedDate(lead: Lead) {
  if (
    lead?.bookedDateTimeString &&
    (!lead.bookedDate || !isDate(lead.bookedDate))
  ) {
    lead.bookedDate = parseUtcDateTimeInLeadTimezone(
      lead,
      lead.bookedDateTimeString,
    );
  }

  return lead?.bookedDate;
}

export function getLeadDetailLocalizedIntValue(
  value: number,
  baseLocaleKey: string,
  t: TFunction,
) {
  if (!value || value < 0) {
    return '';
  }

  if (value === 1) {
    return t(normalizeKey(`componentLead.details.${baseLocaleKey}Single`));
  }

  return t(normalizeKey(`componentLead.details.${baseLocaleKey}Number`), {
    number: value,
  });
}

export const getLeadDetailLocalizedObjectValue = (
  value: number,
  baseLocaleKey: string,
  t: TFunction,
): {
  counter: number;
  label: string;
} => {
  const localizedIntValue = getLeadDetailLocalizedIntValue(
    value,
    baseLocaleKey,
    t,
  );

  const match = localizedIntValue.match(/^(\d+)\s*(\S+)/);

  // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
  if (!match) return null;

  const counter = parseInt(match[1], 10);
  const label = match[2];

  return { counter, label };
};

export function getLeadLocalizedNightsCount(lead: Lead, t: TFunction) {
  const checkInDate = getLeadCheckInDate(lead);
  const checkOutDate = getLeadCheckOutDate(lead);
  const nightCount = differenceInCalendarDays(checkOutDate, checkInDate);

  return getLeadDetailLocalizedIntValue(nightCount, 'night', t);
}

export function getThreadRelatedLeadCheckInDate(lead: ThreadRelatedLead) {
  if (!lead.leadCheckInDateObj) {
    lead.leadCheckInDateObj = parseISO(lead.leadCheckInDate);
  }

  return lead.leadCheckInDateObj;
}

export function getThreadRelatedLeadCheckOutDate(lead: ThreadRelatedLead) {
  if (!lead.leadCheckOutDateObj) {
    lead.leadCheckOutDateObj = parseISO(lead.leadCheckOutDate);
  }

  return lead.leadCheckOutDateObj;
}

export function createThreadRelatedLeadFromLead(
  lead: Lead | null,
  threadUid: string,
): ThreadRelatedLead {
  if (!lead) {
    // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    return null;
  }

  const {
    uid: leadUid,
    // @ts-expect-error TS2339 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    channel: { channel: leadChannel },
    checkInLocalDateTime: leadCheckInDate,
    checkOutLocalDateTime: leadCheckOutDate,
    property: { uid: propertyUid },
    source: leadSource,
    status: leadStatus,
  } = lead;

  return {
    leadUid,
    threadUid,
    leadChannel,
    leadCheckInDate,
    leadCheckOutDate,
    leadSource,
    leadStatus,
    // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    propertyUid,
  };
}

export function convertLeadChannelFromLeadSource(
  leadSource: LeadSource,
): LeadChannel {
  switch (leadSource) {
    case LeadSource.DIRECT_HOMETOGO:
      return LeadChannel.HOMETOGO;
    case LeadSource.IMPORT_TRIPADVISOR:
      return LeadChannel.TRIP_ADVISOR;
    case LeadSource.IMPORT_BOOKINGCOM:
    case LeadSource.DIRECT_BOOKINGDOTCOM:
      return LeadChannel.BOOKING_COM;
    case LeadSource.IMPORT_AIRBNB:
    case LeadSource.DIRECT_AIRBNB:
    case LeadSource.AIRBNB_BLOCK_IMPORT:
      return LeadChannel.AIRBNB;
    case LeadSource.DIRECT_HOLIDU:
      return LeadChannel.HOLIDU;
    case LeadSource.DIRECT_GOOGLE:
      return LeadChannel.GOOGLE;
    case LeadSource.MisterBnB:
      return LeadChannel.MISTERBNB;
    case LeadSource.DIRECT_REDAWNING:
      return LeadChannel.REDAWNING;
    case LeadSource.DIRECT_HVMI:
      return LeadChannel.HVMI;
    case LeadSource.DIRECT_HOMEAWAY:
    case LeadSource.IMPORT_HOMEAWAY:
    case LeadSource.IMPORT_ABRITEL:
    case LeadSource.IMPORT_VRBO:
      return LeadChannel.VRBO;
    default:
      return LeadChannel.HOSTFULLY;
  }
}

export function convertLeadSourceFromLeadChannel(
  leadChannel: LeadChannel,
): LeadSource {
  return ChannelToSourceMap.get(leadChannel) || LeadSource.ORBIRENTAL_FORM;
}

export function convertLeadChannelToAffiliate(
  leadChannel: LeadChannel,
): IntegrationEnum {
  const channelToIntegrationMap: Record<LeadChannel, IntegrationEnum> = {
    [LeadChannel.HOSTFULLY]: IntegrationEnum.HOSTFULLY,
    [LeadChannel.AIRBNB]: IntegrationEnum.AIRBNB,
    [LeadChannel.BOOKING_COM]: IntegrationEnum.BOOKING_COM,
    [LeadChannel.VRBO]: IntegrationEnum.HOMEAWAY,
    [LeadChannel.HVMI]: IntegrationEnum.HVMI,
    [LeadChannel.GOOGLE]: IntegrationEnum.GOOGLE,
    [LeadChannel.FIND_RENTALS]: IntegrationEnum.FINDRENTALS,
    [LeadChannel.GOLIGHTLY]: IntegrationEnum.GOLIGHTLY,
    [LeadChannel.HOMADS]: IntegrationEnum.HOMADS,
    [LeadChannel.HOMETOGO]: IntegrationEnum.HOMETOGO,
    [LeadChannel.TRIP_ADVISOR]: IntegrationEnum.TRIPADVISOR,
    [LeadChannel.VACAYMYMAY]: IntegrationEnum.VACAYMYWAY,
    [LeadChannel.HOLIDU]: IntegrationEnum.HOLIDU_COM,
    [LeadChannel.STAYMENTITY]: IntegrationEnum.TRAVELSTAYTION,
    [LeadChannel.GETAWAYGOGO]: IntegrationEnum.GETAWAYGOGO,
    [LeadChannel.EVERYSTAY]: IntegrationEnum.HOSTFULLY,
    [LeadChannel.EGETINNZ]: IntegrationEnum.EGETINNZ,
    [LeadChannel.REDAWNING]: IntegrationEnum.REDAWNING,
    [LeadChannel.MISTERBNB]: IntegrationEnum.HOSTFULLY,
    [LeadChannel.WHIMSTAY]: IntegrationEnum.WHIMSTAY,
    [LeadChannel.NEXTPAX]: IntegrationEnum.NEXTPAX,
    [LeadChannel.RENTALS_UNITED]: IntegrationEnum.RENTALS_UNITED,
    [LeadChannel.FURNISHED_FINDER]: IntegrationEnum.FURNISHEDFINDER,
    [LeadChannel.MONAKER_GROUP]: IntegrationEnum.THE_MONAKER_GROUP,
  };

  return channelToIntegrationMap[leadChannel] || IntegrationEnum.HOSTFULLY;
}

export function shouldHandleSecurityDeposit(lead: Lead) {
  if (!lead.order?.securityDeposit) {
    return false;
  }

  return (
    lead.source !== LeadSource.IMPORT_AIRBNB &&
    lead.source !== LeadSource.AIRBNB_BLOCK_IMPORT &&
    lead.source !== LeadSource.DIRECT_AIRBNB &&
    lead.source !== LeadSource.DIRECT_TRIPPING &&
    lead.source !== LeadSource.IMPORT_TRIPADVISOR
  );
}
export function getLabelSource(source: LeadSource) {
  switch (source) {
    case LeadSource.ORBIRENTAL_FORM:
    case LeadSource.ORBIRENTAL_WIDGET:
    case LeadSource.ORBIRENTAL_API:
    case LeadSource.OWNER_PORTAL:
    case LeadSource.ORBIRENTAL_LINKED:
      return 'Hostfully';

    case LeadSource.IMPORT_HOMEAWAY:
    case LeadSource.IMPORT_VRBO:
    case LeadSource.IMPORT_ABRITEL:
    case LeadSource.DIRECT_HOMEAWAY:
      return 'VRBO';

    case LeadSource.IMPORT_TRIPADVISOR:
      return 'Tripadvisor';

    case LeadSource.DIRECT_TRIPPING:
      return 'Tripping';

    case LeadSource.DIRECT_HOMETOGO:
      return 'Hometogo';

    case LeadSource.IMPORT_BOOKINGCOM:
    case LeadSource.DIRECT_BOOKINGDOTCOM:
      return 'Booking.com';

    case LeadSource.AIRBNB_BLOCK_IMPORT:
    case LeadSource.IMPORT_AIRBNB:
    case LeadSource.DIRECT_AIRBNB:
      return 'Airbnb';

    case LeadSource.DIRECT_HOLIDU:
      return 'Holidu';

    case LeadSource.DIRECT_HVMI:
      return 'HVMI';

    case LeadSource.DIRECT_GOOGLE:
      return 'Google';

    default:
      return source.toLowerCase();
  }
}

export function isFromICS(source: LeadSource) {
  let result = false;

  switch (source) {
    case LeadSource.IMPORT_HOMEAWAY:
    case LeadSource.IMPORT_VRBO:
    case LeadSource.IMPORT_ABRITEL:
    case LeadSource.IMPORT_TRIPADVISOR:
    case LeadSource.IMPORT_AIRBNB:
    case LeadSource.IMPORT_BOOKINGCOM:
      result = true;
      break;
    default:
      break;
  }

  return result;
}

export function isLeadFieldDisabled(
  lead: Lead | undefined,
  fieldName: string,
): boolean {
  if (!lead) return false;

  return (
    lead.fieldsPermissionsMetadata?.find((fpm) => fpm.fieldName === fieldName)
      ?.metadata?.editable === false || false
  );
}

export function getOpacity(status: LeadStatus) {
  if (TRANSPARENT_STATUSES.includes(status)) {
    return 0.5;
  }
  return 1.0;
}

// @ts-expect-error TS7006 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
export function getZIndexFromOpacity(opacity) {
  if (opacity === 0) return 0;
  return (1 / opacity) * 98;
}

export const getZIndexFromLeadSource = (
  source: string,
  status: string,
): number => {
  if (source.includes('IMPORT')) {
    return 50;
  }

  if (status === LeadStatus.BLOCKED) {
    return 100;
  }

  return 200;
};

export const getLabelZIndexFromLeadSource = (
  source: string,
  status: string,
): number => {
  return getZIndexFromLeadSource(source, status) + 1;
};

export function getFullName(
  lead: Lead | CalendarLead,
  firstHalfEmailOnly?: boolean,
) {
  const fullName = [];

  if (lead.firstName) fullName.push(lead.firstName);
  if (lead.lastName) fullName.push(lead.lastName);
  if (!lead.firstName && !lead.lastName)
    fullName.push(
      firstHalfEmailOnly
        ? // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          lead.email.substring(0, lead.email.indexOf('@'))
        : lead.email,
    );
  return fullName.join(' ');
}

export function getColorByAffiliate(affiliate: IntegrationEnum | null) {
  if (!affiliate) {
    return LeadColor.DEFAULT;
  }

  return AffiliateToColorMap.get(affiliate) || LeadColor.DEFAULT;
}

export const getTextColorByChannel = (channel: string) => {
  switch (channel) {
    case LeadChannel.AIRBNB:
      return '#ff5a5f';
    case LeadChannel.HOSTFULLY:
      return '#ffc454';
    default:
      return '#3ECB9A';
  }
};

export const blockedLeadGradientPrimaryColor = {
  owner: 'rgba(119, 55, 154, 0.7)',
  other: 'rgba(48, 175, 139, 0.7)',
};

export function getLeadColor(
  mode: StackedCalendarMode | null,
  lead: Pick<Lead, 'status' | 'creator' | 'affiliate'>,
) {
  if (lead.status === LeadStatus.BLOCKED) {
    const { owner, other } = blockedLeadGradientPrimaryColor;

    if (lead.creator && lead.creator.type === 'OWNER') {
      if (mode === StackedCalendarMode.BOOKING) {
        return `repeating-linear-gradient(45deg, ${owner}, ${owner} 6px, rgba(119, 55, 154,1) 5px, rgba(119, 55, 154,1) 12px)`;
      }

      return `repeating-linear-gradient(45deg, ${owner}, ${owner} 5px, rgba(119, 55, 154,1) 5px, rgba(119, 55, 154,1) 10px)`;
    }

    if (mode === StackedCalendarMode.BOOKING) {
      return `repeating-linear-gradient(45deg, ${other}, ${other} 6px,rgba(48,175,139,1) 5px,rgba(48,175,139,1) 12px)`;
    }
    return `repeating-linear-gradient(45deg, ${other}, ${other} 5px, rgba(48, 175, 139,1) 5px, rgba(48, 175, 139,1) 10px)`;
  }

  return getColorByAffiliate(lead.affiliate);
}

const sourcesAllowedForSendingMessage = [
  LeadSource.DIRECT_AIRBNB,
  LeadSource.DIRECT_BOOKINGDOTCOM,
  LeadSource.DIRECT_HOMEAWAY,
  LeadSource.DIRECT_HVMI,
  LeadSource.DIRECT_GOOGLE,
  LeadSource.ORBIRENTAL_API,
  LeadSource.ORBIRENTAL_FORM,
  LeadSource.ORBIRENTAL_WIDGET,
  LeadSource.DIRECT_REDAWNING,
];

export function canSendMessage({ source, status }: Lead) {
  return (
    status !== LeadStatus.BLOCKED &&
    sourcesAllowedForSendingMessage.includes(source)
  );
}

const bookedStatuses = [
  LeadStatus.BOOKED_BY_AGENT,
  LeadStatus.BOOKED_BY_CUSTOMER,
  LeadStatus.BOOKED_BY_OWNER,
  LeadStatus.ARCHIVED,
  LeadStatus.STAY,
  LeadStatus.PAID_IN_FULL,
  LeadStatus.BOOKED_EXTERNALLY,
];

export const isLeadBooked = (lead: Lead) => {
  if (!lead) {
    return false;
  }

  return bookedStatuses.includes(lead.status);
};

export function canEditQuote(lead: Lead) {
  if (!lead.uid) return true;

  if (
    lead.source === LeadSource.DIRECT_AIRBNB ||
    lead.source === LeadSource.DIRECT_TRIPPING ||
    lead.source === LeadSource.DIRECT_BOOKINGDOTCOM
  ) {
    return false;
  }

  switch (lead.status) {
    case LeadStatus.NEW:
    case LeadStatus.QUOTE_SENT:
    case LeadStatus.ON_HOLD:
    case LeadStatus.HOLD_EXPIRED:
    case LeadStatus.BOOKED_BY_AGENT:
    case LeadStatus.BOOKED_BY_CUSTOMER:
    case LeadStatus.BLOCKED:
    case LeadStatus.SAMPLE:
    case LeadStatus.BOOKED_EXTERNALLY:
    case LeadStatus.PAID_IN_FULL:
    case LeadStatus.STAY:
    case LeadStatus.ARCHIVED:
      return true;
    default:
      return false;
  }
}

export const canAttributeLeadToChannel = (lead?: Lead) => {
  if (!lead) return false;

  return (
    (lead.source === LeadSource.ORBIRENTAL_FORM ||
      lead.source === LeadSource.ORBIRENTAL_WIDGET ||
      lead.source === LeadSource.ORBIRENTAL_API) &&
    isLeadBooked(lead)
  );
};

// Derives the channel a lead was attributed to from its source (the source is
// the source of truth for attribution). We intentionally do NOT read
// lead.channel here: the backend resolves the display channel from the
// affiliate, which attribution does not update, so an attributed lead can come
// back with channel=HOSTFULLY even though its source is DIRECT_FURNISHEDFINDER.
export const getCurrentAttributedChannel = (
  lead: Lead | undefined,
): IntegrationEnum | undefined => {
  switch (lead?.source) {
    case LeadSource.DIRECT_HOMEAWAY:
      return IntegrationEnum.HOMEAWAY;
    case LeadSource.DIRECT_FURNISHEDFINDER:
      return IntegrationEnum.FURNISHEDFINDER;
    default:
      return undefined;
  }
};

type AttributableChannel = {
  channelEnumId: Channel;
  value: IntegrationEnum;
};

export const ATTRIBUTABLE_CHANNELS = [
  {
    channelEnumId: Channel.homeaway,
    value: IntegrationEnum.HOMEAWAY,
  },
  {
    channelEnumId: Channel.furnishedfinder,
    value: IntegrationEnum.FURNISHEDFINDER,
  },
] as const satisfies readonly AttributableChannel[];

export const getAttributableChannels = (
  lead: Lead | undefined,
  channelSettings: ChannelSettings[] | undefined,
) => {
  if (!canAttributeLeadToChannel(lead) || !channelSettings) return [];

  return ATTRIBUTABLE_CHANNELS.filter(({ channelEnumId }) =>
    channelSettings.some((cs) => cs.enumId === channelEnumId && cs.isActive),
  );
};

// Options for the "Attribute this booking to" select.
// When the lead is still attributable, list the active attributable channels.
// Otherwise, if the lead is already attributed, surface just that channel so
// the (disabled) select shows its label instead of falling back to "None".
export const getAttributedChannelSelectOptions = (
  lead: Lead | undefined,
  channelSettings: ChannelSettings[] | undefined,
) => {
  const attributable = getAttributableChannels(lead, channelSettings);
  if (attributable.length > 0) {
    return attributable;
  }

  const current = getCurrentAttributedChannel(lead);
  return ATTRIBUTABLE_CHANNELS.filter(({ value }) => value === current);
};

const sourcesNotAllowedToBeDeleted = [
  LeadSource.DIRECT_AIRBNB,
  LeadSource.DIRECT_TRIPPING,
  LeadSource.DIRECT_EXPEDIA,
  LeadSource.DIRECT_HOLIDU,
  LeadSource.DIRECT_TRIPPING,
  LeadSource.DIRECT_HOMEAWAY,
  LeadSource.DIRECT_BOOKINGDOTCOM,
];
const statusesAlwaysAllowedToBeDeleted = [
  LeadStatus.CANCELLED,
  LeadStatus.CANCELLED_BY_OWNER,
  LeadStatus.CANCELLED_BY_TRAVELER,
];
const statusesAllowedToBeDeletedIfSourceAllows = [
  LeadStatus.NEW,
  LeadStatus.HOLD_EXPIRED,
  LeadStatus.BLOCKED,
  LeadStatus.QUOTE_SENT,
  LeadStatus.DECLINED,
  LeadStatus.CANCELLED,
  LeadStatus.CANCELLED_BY_TRAVELER,
  LeadStatus.CANCELLED_BY_OWNER,
  LeadStatus.IGNORED,
  LeadStatus.DUPLICATE,
  LeadStatus.SAMPLE,
  LeadStatus.CLOSED_QUOTE,
  LeadStatus.CLOSED_HOLD,
  LeadStatus.PENDING,
  LeadStatus.ARCHIVED,
];

export const cancelledStatus = statusesAlwaysAllowedToBeDeleted;

export function canBeDeleted(lead: Lead | undefined) {
  if (!lead || !lead.uid) {
    return false;
  }

  const { source, status } = lead;

  return (
    statusesAlwaysAllowedToBeDeleted.includes(status) ||
    (status === LeadStatus.BLOCKED &&
      source === LeadSource.DIRECT_BOOKINGDOTCOM) ||
    (!sourcesNotAllowedToBeDeleted.includes(source) &&
      statusesAllowedToBeDeletedIfSourceAllows.includes(status))
  );
}

export function canOpenMailerOnSave({
  source,
  status,
}: {
  source: LeadSource;
  status: LeadStatus;
}) {
  return (
    status === LeadStatus.NEW ||
    status === LeadStatus.QUOTE_SENT ||
    status === LeadStatus.BOOKED_BY_AGENT ||
    status === LeadStatus.ON_HOLD ||
    (status === LeadStatus.BOOKED_EXTERNALLY &&
      source === LeadSource.ORBIRENTAL_API)
  );
}

export function isDuplicate({ duplicateOfLeadUid, status }: Lead) {
  return (
    ((status === LeadStatus.NEW || status === LeadStatus.CLOSED_QUOTE) &&
      !!duplicateOfLeadUid) ||
    status === LeadStatus.DUPLICATE
  );
}

export function getTemplateNameByLeadStatus(
  status: LeadStatus,
): TemplateName | null {
  if (status === LeadStatus.NEW || status === LeadStatus.QUOTE_SENT) {
    return TemplateName.QUOTE;
  }

  if (status === LeadStatus.ON_HOLD) {
    return TemplateName.HOLD_CONFIRMATION;
  }

  if (
    status === LeadStatus.BOOKED_BY_AGENT ||
    status === LeadStatus.BOOKED_EXTERNALLY
  ) {
    return TemplateName.BALANCE_DUE;
  }

  return null;
}

export const canLeadViewPaymentLink = (lead: Lead) => {
  if (
    lead.source === LeadSource.ORBIRENTAL_FORM ||
    lead.source === LeadSource.ORBIRENTAL_WIDGET ||
    lead.source === LeadSource.ORBIRENTAL_API ||
    lead.source === LeadSource.IMPORT_BOOKINGCOM ||
    lead.source === LeadSource.DIRECT_BOOKINGDOTCOM ||
    lead.source === LeadSource.DIRECT_HOMEAWAY ||
    lead.source === LeadSource.DIRECT_GOOGLE
  ) {
    if (
      lead.status === LeadStatus.HOLD_EXPIRED ||
      lead.status === LeadStatus.BLOCKED ||
      lead.status === LeadStatus.DECLINED ||
      lead.status === LeadStatus.CANCELLED ||
      lead.status === LeadStatus.CANCELLED_BY_TRAVELER ||
      lead.status === LeadStatus.CANCELLED_BY_OWNER ||
      lead.status === LeadStatus.IGNORED ||
      // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      (lead.status === LeadStatus.PAID_IN_FULL && lead.order?.balance <= 0) ||
      isDuplicate(lead) ||
      lead.status === LeadStatus.SAMPLE ||
      lead.status === LeadStatus.CLOSED_QUOTE ||
      lead.status === LeadStatus.CLOSED_HOLD ||
      lead.status === LeadStatus.PENDING
    ) {
      return false;
    }

    // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    if (lead.status === LeadStatus.ARCHIVED && lead.order?.balance <= 0) {
      return false;
    }

    return true;
  }

  return false;
};

export const getPaymentLink = (lead: Lead) => {
  return lead.order?.paymentLinkUrl;
};

export const isLeadChannelImportsDiscount = (lead: Lead) => {
  return lead?.source === LeadSource.DIRECT_HVMI;
};

export const isVrboOfflineLead = (lead?: Lead | null): boolean =>
  lead?.vrboMetadata?.offlineLead === true;

/**
 * Helper function to get the unit type from a property uid (unit uid),
 * useful to send the unitType as the propertyUid and unitUid in the backend.
 * @param availableProperties List of properties.
 * @param propertyUid The property uid that can be a unit, to find the unit type from it.
 * @returns Return the unit type, if available, null if not available.
 */
export const getUnitType = (
  availableProperties: Property[],
  propertyUid: string,
) => {
  return (
    availableProperties
      ?.flatMap((property) => property?.unitTypes || [])
      ?.find((ut) => ut?.units?.some((unit) => unit?.uid === propertyUid)) ||
    null
  );
};

export const computeBestProfile = (lead: LeadsSearchResponseItem) => {
  if (!lead) return '';

  if (lead.guestInformation?.firstName && lead.guestInformation?.lastName) {
    return `${lead.guestInformation?.firstName} ${lead.guestInformation?.lastName}`;
  }
  if (lead.guestInformation?.firstName) {
    return `${lead.guestInformation?.firstName}`;
  }
  if (lead.guestInformation.email) {
    const email = lead.guestInformation?.email;
    return `${email.substring(0, email.indexOf('@'))}`;
  }

  return '';
};

export function isLeadEditable(lead: Lead) {
  return (
    !lead?.uid ||
    (lead &&
      !SOURCES_ORDER_NOT_EDITABLE.includes(lead?.source) &&
      canEditQuote(lead))
  );
}

export function isLeadImported(lead: Lead) {
  return lead && IMPORT_SOURCES.includes(lead?.source);
}

export function showCleaningFeeRow({
  lead,
  cleaningFee,
  allowEdit,
}: {
  lead: Lead;
  cleaningFee: HasCleaningFeeItem;
  allowEdit: boolean;
}) {
  const isAirbnbInquiry =
    lead?.source === LeadSource.DIRECT_AIRBNB &&
    lead?.leadType === LeadType.INQUIRY;

  return (
    !isAirbnbInquiry && cleaningFee && (cleaningFee.netPrice > 0 || allowEdit)
  );
}

export function isLeadAMainBooking(lead: Lead) {
  return lead?.uid === lead?.groupUid && lead?.isGroupBooking;
}

export function isLeadASubBooking(
  lead: Lead,
): lead is Lead & { groupUid: string } {
  return Boolean(
    lead?.groupUid && !isLeadAMainBooking(lead) && lead?.isGroupBooking,
  );
}

/**
 * A sub-booking is labelled with its group booking's main booking name. The
 * main booking can be absent from the map when it belongs to a property the
 * current user cannot access, so the sub-booking itself is the fallback.
 */
export function getGroupBookingLabelLead(
  lead: Lead,
  leadsMap: Record<string, Lead | CalendarLead>,
): Lead | CalendarLead {
  if (!isLeadASubBooking(lead)) {
    return lead;
  }

  return leadsMap[lead.groupUid] ?? lead;
}

export function canAddNewLineItem(lead: Lead) {
  return !isLeadImported(lead) && !isLeadAMainBooking(lead);
}

export function canForceFullPayment(lead: Lead) {
  if (isLeadAMainBooking(lead)) return false;

  if (
    lead.source === LeadSource.ORBIRENTAL_FORM ||
    lead.source === LeadSource.ORBIRENTAL_WIDGET ||
    lead.source === LeadSource.ORBIRENTAL_API ||
    lead.source === LeadSource.IMPORT_BOOKINGCOM
  ) {
    switch (lead.status) {
      case LeadStatus.NEW:
      case LeadStatus.QUOTE_SENT:
      case LeadStatus.ON_HOLD:
      case LeadStatus.HOLD_EXPIRED:
      case LeadStatus.BOOKED_EXTERNALLY:
        return true;
      default:
        return false;
    }
  }

  return false;
}

const leadDocumentsPath = (leadUid: string) =>
  `/api/internal/leads/${leadUid}/documents`;

export function getLeadRentalAgreementDocumentUrl(leadUid?: string) {
  if (!leadUid) {
    return undefined;
  }

  return `${leadDocumentsPath(leadUid)}/rental-agreement`;
}

export function getLeadIdentificationDocumentUrl(leadUid?: string) {
  if (!leadUid) {
    return undefined;
  }

  return `${leadDocumentsPath(leadUid)}/identification`;
}

export function getExtraGuestIdentificationDocumentUrl(
  leadUid?: string,
  extraGuestId?: number | null,
) {
  if (!leadUid || extraGuestId == null) {
    return undefined;
  }

  return `${leadDocumentsPath(leadUid)}/identification/${extraGuestId}`;
}
